import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Sidebar, Heading, Text, Input, Button, LoadingSpinner, SortableTableHeader } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { modulesService } from '../services/modules'
import { dashcliService } from '../services/dashcli'
import { DashCLICompany, HistoricoAcesso } from '../types/dashcli'
import { useAuthContext } from '../contexts/AuthContext'
import { useUserLogs } from '../hooks/useUserLogs'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

const DashboardClientes: React.FC = () => {
  const { user } = useAuthContext()
  const { logAction, logCreate, logUpdate } = useUserLogs()
  const [activeTab, setActiveTab] = useState<'historico' | 'empresas'>('historico')
  const [clienteQuery, setClienteQuery] = useState('')
  const [cnpjQuery, setCnpjQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [companyCodeFilter, setCompanyCodeFilter] = useState('')
  const [supergroupCodeFilter, setSupergroupCodeFilter] = useState('')
  const [moduleTitle, setModuleTitle] = useState<string>('Dashboard Clientes')
  const [moduleDescription, setModuleDescription] = useState<string>('Visão de acessos por cliente')
  
  // Estados para aba de empresas
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(true)
  const [companies, setCompanies] = useState<DashCLICompany[]>([])
  const [companyQuery, setCompanyQuery] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<DashCLICompany> | null>(null)

  // Estados para aba de histórico
  const [loadingHistorico, setLoadingHistorico] = useState<boolean>(true)
  const [historicoAcessos, setHistoricoAcessos] = useState<HistoricoAcesso[]>([])
  const [totalHistorico, setTotalHistorico] = useState<number>(0)
  const [acessosHoje, setAcessosHoje] = useState<number>(0)
  
  // Estados para ordenação
  const [sortBy, setSortBy] = useState<string>('access_timestamp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // Navegação horizontal da tabela
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTableBy = (dx: number) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: dx, behavior: 'smooth' })
    }
  }
  const handleTableWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Converte scroll vertical do mouse em horizontal
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollBy({ left: e.deltaY, behavior: 'auto' })
      }
    }
  }

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('portais_dashboard_cliente')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Buscar histórico de acessos
  const loadHistoricoAcessos = useCallback(async () => {
    if (activeTab !== 'historico') return

    setLoadingHistorico(true)
    try {
      // Buscar dados em paralelo
      const [historico, total, hoje] = await Promise.all([
        dashcliService.getHistoricoAcessos({
          clienteQuery: clienteQuery || undefined,
          cnpjQuery: cnpjQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          companyCode: companyCodeFilter || undefined,
          supergroupCode: supergroupCodeFilter || undefined
        }),
        dashcliService.getTotalAcessos(),
        dashcliService.getAcessosHoje()
      ])

      setHistoricoAcessos(historico)
      setTotalHistorico(total)
      setAcessosHoje(hoje)
    } catch (error) {
      console.error('Erro ao carregar histórico de acessos:', error)
      // Em caso de erro, definir valores padrão
      setHistoricoAcessos([])
      setTotalHistorico(0)
      setAcessosHoje(0)
    } finally {
      setLoadingHistorico(false)
    }
  }, [activeTab, clienteQuery, cnpjQuery, statusFilter, dateFrom, dateTo, companyCodeFilter, supergroupCodeFilter])

  useEffect(() => {
    loadHistoricoAcessos()
  }, [loadHistoricoAcessos])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  // Funções para aba de empresas
  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true)
    try {
      const data = await dashcliService.listCompanies()
      setCompanies(data)
    } catch (error) {
      console.error('Erro ao carregar empresas:', error)
    } finally {
      setLoadingCompanies(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'empresas') {
      loadCompanies()
    }
  }, [activeTab, loadCompanies])

  const filteredCompanies = useMemo(() => {
    if (!companyQuery) return companies
    const q = companyQuery.toLowerCase()
    return companies.filter(c =>
      c.company_name.toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q) ||
      (c.type || '').toLowerCase().includes(q)
    )
  }, [companies, companyQuery])

  // Função para alternar ordenação
  const toggleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }, [sortBy, sortOrder])

  // Aplicar ordenação e filtros
  const filtrados = useMemo(() => {
    let resultado = [...historicoAcessos]

    // Aplicar ordenação
    resultado.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortBy) {
        case 'cliente':
          aValue = a.cliente || ''
          bValue = b.cliente || ''
          break
        case 'company_id':
          aValue = a.company_id || ''
          bValue = b.company_id || ''
          break
        case 'supergroup_id':
          aValue = a.supergroup_id || ''
          bValue = b.supergroup_id || ''
          break
        case 'access_timestamp':
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
          break
        case 'route':
          aValue = a.route || ''
          bValue = b.route || ''
          break
        case 'status':
          aValue = a.status || ''
          bValue = b.status || ''
          break
        case 'error_message':
          aValue = a.error_message || ''
          bValue = b.error_message || ''
          break
        case 'user_agent':
          aValue = a.user_agent || ''
          bValue = b.user_agent || ''
          break
        default:
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
      }

      // Comparação
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return resultado
  }, [historicoAcessos, sortBy, sortOrder])

  // Calcular dados da paginação
  const totalPages = Math.ceil(filtrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const filtradosPaginados = filtrados.slice(startIndex, endIndex)

  // Handlers de paginação
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage])

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, totalPages])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [clienteQuery, cnpjQuery, statusFilter, dateFrom, dateTo, companyCodeFilter, supergroupCodeFilter])

  const openNewCompany = () => {
    if (!user) return
    setEditing({ 
      company_name: '', 
      code: '', 
      type: 'supergrupo',
      is_active: true,
      owner_user_id: user.id
    })
    setIsModalOpen(true)

    // Registrar log de abertura do formulário de nova empresa
    logAction(
      'view',
      'other',
      null,
      'Abriu formulário para criar nova empresa',
      {
        action: 'abrir_formulario_criacao',
        module: 'portais_dashboard_cliente'
      }
    ).catch((logError) => {
      // Não bloquear a operação em caso de erro no log
      console.warn('Erro ao registrar log de abertura do formulário:', logError)
    })
  }

  const toggleCompanyStatus = async (company: DashCLICompany) => {
    try {
      const oldStatus = company.is_active
      const newStatus = !company.is_active
      
      await dashcliService.upsertCompany({
        ...company,
        is_active: newStatus
      })
      await loadCompanies()

      // Registrar log de alteração de status
      try {
        await logUpdate(
          'other',
          company.id || null,
          `${newStatus ? 'Ativou' : 'Desativou'} empresa ${company.company_name}`,
          { is_active: oldStatus },
          { is_active: newStatus },
          {
            company_id: company.id,
            company_name: company.company_name,
            company_code: company.code,
            company_type: company.type
          }
        )
      } catch (logError) {
        // Não bloquear a operação em caso de erro no log
        console.warn('Erro ao registrar log de alteração de status:', logError)
      }
    } catch (error) {
      console.error('Erro ao alterar status da empresa:', error)
      alert('Erro ao alterar status da empresa. Tente novamente.')
    }
  }

  const openEditCompany = (c: DashCLICompany) => {
    setEditing({ ...c })
    setIsModalOpen(true)
  }

  const handleAcessar = async (company: DashCLICompany) => {
    try {
      // Preparar body baseado no tipo
      const body: { codempresa: string; supergrupo: string } = {
        codempresa: '',
        supergrupo: ''
      }

      if (company.type === 'supergrupo') {
        body.supergrupo = company.code || ''
        body.codempresa = ''
      } else if (company.type === 'codigo_empresa') {
        body.codempresa = company.code || ''
        body.supergrupo = ''
      }

      // Fazer requisição POST para a API
      const response = await fetch('https://webhook.pasiai.com/webhook/GerarLinkDashboardCliente', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        throw new Error(`Erro ao gerar link: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      // Verificar se a resposta contém um link em diferentes formatos possíveis
      let link: string | null = null
      
      if (typeof data === 'string') {
        link = data.trim()
      } else if (data && typeof data === 'object') {
        link = data.link || data.url || data.dashboard_url || data.redirect_url || null
      }
      
      if (link && typeof link === 'string' && link.trim()) {
        // Abrir link em nova aba
        window.open(link.trim(), '_blank')

        // Registrar log de acesso ao dashboard do cliente
        try {
          await logAction(
            'view',
            'other',
            company.id || null,
            `Acessou dashboard do cliente ${company.company_name}`,
            {
              company_id: company.id,
              company_name: company.company_name,
              company_code: company.code,
              company_type: company.type,
              dashboard_url: link.trim(),
              tipo_acesso: company.type === 'supergrupo' ? 'Supergrupo' : 'Código da Empresa',
              origem: 'portais/dashboard_cliente',
            }
          )
        } catch (logError) {
          // Não bloquear a operação em caso de erro no log
          console.warn('Erro ao registrar log de acesso ao dashboard:', logError)
        }
      } else {
        throw new Error('Link não encontrado na resposta da API')
      }
    } catch (error) {
      console.error('Erro ao acessar dashboard do cliente:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    }
  }

  const saveCompany = async () => {
    if (!editing?.company_name || !editing?.code || !user) return
    
    try {
      // Buscar dados antigos se for edição
      let oldValues: Partial<DashCLICompany> | undefined
      if (editing.id) {
        const existingCompany = companies.find(c => c.id === editing.id)
        if (existingCompany) {
          oldValues = {
            company_name: existingCompany.company_name,
            code: existingCompany.code,
            type: existingCompany.type,
            is_active: existingCompany.is_active
          }
        }
      }

      const companyData = {
        ...editing,
        owner_user_id: editing.owner_user_id || user.id
      }
      const result = await dashcliService.upsertCompany(companyData)
      
      // Registrar log
      try {
        if (editing.id) {
          // É uma edição
          await logUpdate(
            'other',
            editing.id,
            `Editou empresa ${editing.company_name}`,
            oldValues,
            {
              company_name: editing.company_name,
              code: editing.code,
              type: editing.type,
              is_active: editing.is_active
            },
            {
              company_id: editing.id,
              owner_user_id: companyData.owner_user_id
            }
          )
        } else {
          // É uma criação - usar o ID retornado pelo upsert
          const newCompanyId = result?.id || null
          await logCreate(
            'other',
            newCompanyId,
            `Criou empresa ${editing.company_name}`,
            {
              company_name: editing.company_name,
              code: editing.code,
              type: editing.type,
              is_active: editing.is_active,
              owner_user_id: companyData.owner_user_id
            }
          )
        }
      } catch (logError) {
        // Não bloquear a operação em caso de erro no log
        console.warn('Erro ao registrar log de empresa:', logError)
      }

      setIsModalOpen(false)
      setEditing(null)
      await loadCompanies()
    } catch (error) {
      console.error('Erro ao salvar empresa:', error)
      alert('Erro ao salvar empresa. Verifique os dados e tente novamente.')
    }
  }


  const getTypeLabel = (type: string) => {
    return type === 'supergrupo' ? 'Supergrupo' : 'Código da Empresa'
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName="portais_dashboard_cliente"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
              <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
            </div>

            {/* Navegação das Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('historico')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'historico'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Histórico de Acessos
                </button>
                <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="insert" fallback={null}>
                  <button
                    onClick={() => setActiveTab('empresas')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'empresas'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Cadastro de Empresas
                  </button>
                </PermissionGuard>
              </nav>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'historico' && (
              <>
            {/* KPIs */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{totalHistorico}</Text>
                    <Text size="sm" className="text-white">Acessos - Histórico</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{acessosHoje}</Text>
                    <Text size="sm" className="text-white">Acessos Hoje</Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Cliente</Text>
                    <Input
                      type="text"
                      placeholder="Digite o nome do cliente..."
                      value={clienteQuery}
                      onChange={(e) => setClienteQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                    <select
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'failed')}
                    >
                      <option value="all">Todos</option>
                      <option value="success">Sucesso</option>
                      <option value="failed">Falha</option>
                    </select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                    <Input
                      type="date"
                      value={dateFrom ? dateFrom.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        // Adicionar hora 00:00:00 para início do dia
                        setDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                    <Input
                      type="date"
                      value={dateTo ? dateTo.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        // Adicionar hora 23:59:59 para fim do dia
                        setDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Código da Empresa</Text>
                    <Input
                      type="text"
                      placeholder="Digite o código..."
                      value={companyCodeFilter}
                      onChange={(e) => setCompanyCodeFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Código do Supergrupo</Text>
                    <Input
                      type="text"
                      placeholder="Digite o código..."
                      value={supergroupCodeFilter}
                      onChange={(e) => setSupergroupCodeFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { 
                      setClienteQuery('')
                      setCnpjQuery('')
                      setStatusFilter('all')
                      setDateFrom('')
                      setDateTo('')
                      setCompanyCodeFilter('')
                      setSupergroupCodeFilter('')
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">Histórico de Acessos</Heading>
                  {!loadingHistorico && filtrados.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollTableBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollTableBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto">
                  {loadingHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div ref={tableScrollRef} className="overflow-x-auto" onWheel={handleTableWheel}>
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader 
                              sortKey="cliente" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Cliente
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="company_id" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Código Cliente
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="supergroup_id" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Código Supergrupo
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="access_timestamp" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data/Hora Acesso
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="route" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Rota
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="status" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Status
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="error_message" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Mensagem de Erro
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="user_agent" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              User Agent
                            </SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filtradosPaginados.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">Nenhum dado disponível.</Text>
                              </td>
                            </tr>
                          ) : (
                            filtradosPaginados.map((r, index) => (
                              <tr key={`${r.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left">
                                  <div className="flex flex-col">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">{r.cliente}</Text>
                                    {r.cnpj && (
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">{r.cnpj}</Text>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono text-xs">{r.company_id || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono text-xs">{r.supergroup_id || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(r.access_timestamp)}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all max-w-xs">{r.route || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    r.status === 'success' 
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {r.status === 'success' ? 'Sucesso' : 'Falha'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.error_message || ''}>
                                    {r.error_message ? (r.error_message.length > 50 ? `${r.error_message.substring(0, 50)}...` : r.error_message) : '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.user_agent || ''}>
                                    {r.user_agent ? (r.user_agent.length > 50 ? `${r.user_agent.substring(0, 50)}...` : r.user_agent) : '-'}
                                  </Text>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {totalPages > 1 && !loadingHistorico && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, filtrados.length)} de {filtrados.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number
                            if (totalPages <= 5) {
                              pageNumber = i + 1
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i
                            } else {
                              pageNumber = currentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {activeTab === 'empresas' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                        Cadastro de Empresas
                      </Heading>
                      <Text size="sm" color="muted" className="mt-2">
                        Gerencie empresas que terão acesso ao dashboard do cliente
                      </Text>
                    </div>
                    <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="insert" fallback={null}>
                      <Button variant="solid" onClick={openNewCompany}>Nova empresa</Button>
                    </PermissionGuard>
                  </div>
                  
                  {/* Filtros e busca */}
                  <div className="mb-4 flex items-center gap-3">
                    <div className="w-full sm:w-80">
                      <Input
                        placeholder="Buscar por nome, código ou tipo..."
                        value={companyQuery}
                        onChange={(e: any) => setCompanyQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Tabela */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/30">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome da Empresa</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {loadingCompanies ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center">
                              <LoadingSpinner />
                            </td>
                          </tr>
                        ) : filteredCompanies.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                              Nenhuma empresa encontrada.
                            </td>
                          </tr>
                        ) : (
                          filteredCompanies.map((c) => (
                            <tr key={c.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${!c.is_active ? 'opacity-60' : ''}`}>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{c.company_name}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.code}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                                {getTypeLabel(c.type)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  c.is_active 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {c.is_active ? 'Ativa' : 'Inativa'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right space-x-2">
                                <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="update" fallback={null}>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => toggleCompanyStatus(c)}
                                    className={c.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                                  >
                                    {c.is_active ? 'Desativar' : 'Ativar'}
                                  </Button>
                                </PermissionGuard>
                                <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="update" fallback={null}>
                                  <Button variant="outline" size="sm" onClick={() => openEditCompany(c)}>Editar</Button>
                                </PermissionGuard>
                                {c.is_active && (
                                  <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="update" fallback={null}>
                                    <Button variant="outline" size="sm" onClick={() => handleAcessar(c)}>Acessar</Button>
                                  </PermissionGuard>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Modal */}
                  {isModalOpen && editing && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Text size="lg" className="text-gray-900 dark:text-white">
                            {editing?.id ? 'Editar empresa' : 'Nova empresa'}
                          </Text>
                          <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>
                            Fechar
                          </Button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                              Nome da Empresa *
                            </Text>
                            <Input
                              value={editing.company_name || ''}
                              onChange={(e: any) => setEditing((prev) => ({ ...prev!, company_name: e.target.value }))}
                              placeholder="Nome da empresa"
                              required
                            />
                          </div>
                          <div>
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                              Código *
                            </Text>
                            <Input
                              value={editing.code || ''}
                              onChange={(e: any) => setEditing((prev) => ({ ...prev!, code: e.target.value }))}
                              placeholder="Código numérico"
                              required
                            />
                          </div>
                          <div>
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                              Tipo *
                            </Text>
                            <select
                              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                              value={editing.type || 'supergrupo'}
                              onChange={(e) => setEditing((prev) => ({ ...prev!, type: e.target.value as 'supergrupo' | 'codigo_empresa' }))}
                            >
                              <option value="supergrupo">Supergrupo</option>
                              <option value="codigo_empresa">Código da Empresa</option>
                            </select>
                          </div>
                          <div>
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={editing.is_active !== false}
                                onChange={(e) => setEditing((prev) => ({ ...prev!, is_active: e.target.checked }))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                              />
                              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                Empresa ativa
                              </Text>
                            </label>
                          </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                          <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>
                            Cancelar
                          </Button>
                          {editing?.id ? (
                            <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="update" fallback={null}>
                              <Button 
                                variant="solid" 
                                onClick={saveCompany}
                                disabled={!editing.company_name || !editing.code}
                              >
                                Salvar
                              </Button>
                            </PermissionGuard>
                          ) : (
                            <PermissionGuard moduleName="portais_dashboard_cliente" permissionType="insert" fallback={null}>
                              <Button 
                                variant="solid" 
                                onClick={saveCompany}
                                disabled={!editing.company_name || !editing.code}
                              >
                                Salvar
                              </Button>
                            </PermissionGuard>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default DashboardClientes


